import type {
  PendingRecordingSegmentSettlement,
  RecordingSegmentSettlementStore,
} from './types';

const DATABASE_NAME = 'vd-recorder-segment-settlements-v1';
const DATABASE_VERSION = 1;
const STORE_NAME = 'settlements';

let openPromise: Promise<IDBDatabase> | null = null;

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Settlement storage request failed.'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('Settlement transaction aborted.'));
    transaction.onerror = () => reject(transaction.error ?? new Error('Settlement transaction failed.'));
  });
}

function validSettlement(value: unknown): value is PendingRecordingSegmentSettlement {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PendingRecordingSegmentSettlement>;
  return typeof candidate.key === 'string'
    && candidate.key.length > 0
    && typeof candidate.ownerId === 'string'
    && candidate.ownerId.length > 0
    && typeof candidate.recordingId === 'string'
    && candidate.recordingId.length > 0
    && typeof candidate.segmentId === 'string'
    && candidate.segmentId.length > 0
    && (candidate.action === 'close' || candidate.action === 'cancel')
    && (candidate.state === 'pending' || candidate.state === 'terminal' || candidate.state === 'cleanup')
    && (candidate.action !== 'close'
      || (Number.isSafeInteger(candidate.endMs) && (candidate.endMs ?? -1) >= 0))
    && typeof candidate.createdAt === 'string'
    && Number.isFinite(Date.parse(candidate.createdAt))
    && typeof candidate.expiresAt === 'string'
    && Number.isFinite(Date.parse(candidate.expiresAt))
    && typeof candidate.lastAttemptAt === 'string'
    && Number.isFinite(Date.parse(candidate.lastAttemptAt))
    && Number.isSafeInteger(candidate.attempts)
    && (candidate.attempts ?? -1) >= 0;
}

function openDatabase(): Promise<IDBDatabase> {
  if (openPromise) return openPromise;
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is unavailable in this browser.'));
  }
  let promise!: Promise<IDBDatabase>;
  let abandoned = false;
  promise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (database.objectStoreNames.contains(STORE_NAME)) return;
      const store = database.createObjectStore(STORE_NAME, { keyPath: 'key' });
      store.createIndex('ownerId', 'ownerId', { unique: false });
      store.createIndex('recordingId', 'recordingId', { unique: false });
      store.createIndex('expiresAt', 'expiresAt', { unique: false });
    };
    request.onsuccess = () => {
      const database = request.result;
      if (abandoned) {
        database.close();
        return;
      }
      database.onversionchange = () => {
        database.close();
        if (openPromise === promise) openPromise = null;
      };
      resolve(database);
    };
    request.onerror = () => {
      if (openPromise === promise) openPromise = null;
      reject(request.error ?? new Error('Unable to open settlement storage.'));
    };
    request.onblocked = () => {
      abandoned = true;
      if (openPromise === promise) openPromise = null;
      reject(new Error('Settlement storage upgrade is blocked by another tab.'));
    };
  });
  openPromise = promise;
  return promise;
}

async function listByIndex(
  database: IDBDatabase,
  indexName: 'ownerId' | 'recordingId',
  value: string,
): Promise<PendingRecordingSegmentSettlement[]> {
  const transaction = database.transaction(STORE_NAME, 'readonly');
  const done = transactionDone(transaction);
  let rows: unknown[];
  try {
    rows = await requestValue(
      transaction.objectStore(STORE_NAME).index(indexName).getAll(IDBKeyRange.only(value)),
    ) as unknown[];
    await done;
  } catch (error) {
    await done.catch(() => {});
    throw error;
  }
  return rows.filter(validSettlement).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

async function deleteByIndex(
  database: IDBDatabase,
  indexName: 'recordingId' | 'expiresAt',
  range: IDBKeyRange,
): Promise<void> {
  const transaction = database.transaction(STORE_NAME, 'readwrite');
  const done = transactionDone(transaction);
  const store = transaction.objectStore(STORE_NAME);
  const cursorRequest = store.index(indexName).openKeyCursor(range);
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (!cursor) return;
    store.delete(cursor.primaryKey);
    cursor.continue();
  };
  await done;
}

/**
 * Metadata-only outbox kept separate from raw recorder blobs. Sign-out purges
 * video and audio while preserving same-owner boundary recovery until expiry.
 */
export class IndexedDbSegmentSettlementStore implements RecordingSegmentSettlementStore {
  async putSegmentSettlement(settlement: PendingRecordingSegmentSettlement): Promise<void> {
    if (!validSettlement(settlement)) throw new Error('Invalid recording segment settlement.');
    const database = await openDatabase();
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore(STORE_NAME).put(settlement);
    await done;
  }

  async putSegmentSettlementIfState(
    settlement: PendingRecordingSegmentSettlement,
    expectedState: PendingRecordingSegmentSettlement['state'] | null,
  ): Promise<boolean> {
    if (!validSettlement(settlement)) throw new Error('Invalid recording segment settlement.');
    const database = await openDatabase();
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const done = transactionDone(transaction);
    const store = transaction.objectStore(STORE_NAME);
    let current: unknown;
    try {
      current = await requestValue(store.get(settlement.key));
      const matches = expectedState === null
        ? current === undefined
        : validSettlement(current) && current.state === expectedState;
      if (matches) store.put(settlement);
      await done;
      return matches;
    } catch (error) {
      await done.catch(() => {});
      throw error;
    }
  }

  async deleteSegmentSettlement(key: string): Promise<void> {
    const database = await openDatabase();
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore(STORE_NAME).delete(key);
    await done;
  }

  async listSegmentSettlementsForRecording(
    recordingId: string,
  ): Promise<PendingRecordingSegmentSettlement[]> {
    return listByIndex(await openDatabase(), 'recordingId', recordingId);
  }

  async listSegmentSettlementsForOwner(
    ownerId: string,
  ): Promise<PendingRecordingSegmentSettlement[]> {
    return listByIndex(await openDatabase(), 'ownerId', ownerId);
  }

  async deleteSegmentSettlementsForRecording(recordingId: string): Promise<void> {
    await deleteByIndex(await openDatabase(), 'recordingId', IDBKeyRange.only(recordingId));
  }

  async pruneExpiredSegmentSettlements(now: Date): Promise<void> {
    await deleteByIndex(
      await openDatabase(),
      'expiresAt',
      IDBKeyRange.upperBound(now.toISOString()),
    );
  }
}

/** Test-only reset. Production retains metadata until retry, discard, or expiry. */
export async function resetSegmentSettlementDatabaseForTests(): Promise<void> {
  const database = openPromise ? await openPromise.catch(() => null) : null;
  database?.close();
  openPromise = null;
  if (typeof indexedDB === 'undefined') return;
  await requestValue(indexedDB.deleteDatabase(DATABASE_NAME));
}
